import { FC, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useMixpanel } from 'react-mixpanel-browser';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import AiMarkdownLink from 'modules/ai/components/AiMarkdownLink';
import AiMessageFeedback from 'modules/ai/components/AiMessageFeedback';
import { AI_EVENTS } from 'modules/ai/constants/aiAnalyticsEvents';
import { useAiPanelLinksEnabled } from 'modules/ai/hooks/useAiPanelLinksEnabled';
import trackAiActivity from 'modules/ai/utils/trackAiActivity';
import {
  stripLegacyPanelLinkPrefix,
  transformAiMarkdownLinkUri,
} from 'modules/ai/utils/resolveAiPanelLink';

import ThinkingDots from './ThinkingDots';
import NaviOrb from './NaviOrb';

type AssistantMessageProps = {
  messageId?: string;
  content?: string;
  userQuestion?: string;
  conversationId?: string | null;
  feedback?: 'positive' | 'negative' | null;
  thinking?: boolean;
  streaming?: boolean;
};

const AssistantMessage: FC<AssistantMessageProps> = ({
  messageId,
  content,
  userQuestion = '',
  conversationId = null,
  feedback = null,
  thinking = false,
  streaming = false,
}) => {
  const { societyId = '' } = useParams<{ societyId: string }>();
  const { t } = useTranslate();
  const mixpanel = useMixpanel();
  const panelLinksEnabled = useAiPanelLinksEnabled();

  const displayContent = useMemo(() => {
    if (!panelLinksEnabled || !content) {
      return content ?? '';
    }
    return stripLegacyPanelLinkPrefix(content);
  }, [content, panelLinksEnabled]);

  const handleCopy = useCallback(async () => {
    if (!content || !messageId) return;
    try {
      await navigator.clipboard.writeText(content);
      trackAiActivity({
        mixpanel,
        state: store.getState() as State,
        eventName: AI_EVENTS.MESSAGE_COPIED,
        extras: {
          conversationId,
          messageId,
          responseLength: content.length,
        },
      });
    } catch {
      // clipboard unavailable
    }
  }, [content, conversationId, messageId, mixpanel]);

  const markdownComponents = useMemo(() => {
    if (!panelLinksEnabled) {
      return undefined;
    }
    return {
      // eslint-disable-next-line react/no-unstable-nested-components
      a: ({
        href,
        children,
      }: {
        href?: string; // eslint-disable-line
        children?: React.ReactNode; // eslint-disable-line
      }) => (
        <AiMarkdownLink
          href={href}
          societyId={societyId}
          conversationId={conversationId}
          messageId={messageId}
        >
          {children}
        </AiMarkdownLink>
      ),
    };
  }, [conversationId, messageId, panelLinksEnabled, societyId]);

  const showActions = !thinking && !streaming && !!messageId && !!content;

  return (
    <div className="navi-assistant-message">
      <div className="navi-assistant-message__orb">
        <NaviOrb size={28} showSparkles={false} />
      </div>
      <div className="navi-assistant-message__content">
        <div className="navi-assistant-message__bubble">
          {thinking ? (
            <ThinkingDots />
          ) : (
            <div
              className={`navi-assistant-message__markdown${
                streaming ? ' navi-assistant-message__markdown--streaming' : ''
              }`}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
                transformLinkUri={
                  panelLinksEnabled ? transformAiMarkdownLinkUri : undefined
                }
              >
                {displayContent}
              </ReactMarkdown>
            </div>
          )}
        </div>
        {showActions && (
          <div className="navi-assistant-message__actions">
            <button
              type="button"
              className="navi-assistant-message__copy-btn"
              onClick={handleCopy}
              aria-label={t('Copy')}
              data-testid={`ai-message-copy-${messageId}`}
            >
              <em className="icon ni ni-copy" aria-hidden />
            </button>
            <AiMessageFeedback
              messageId={messageId}
              conversationId={conversationId}
              societyId={societyId}
              userQuestion={userQuestion}
              response={content}
              initialFeedback={feedback}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AssistantMessage;
